import type { CalibrationInitializationOptions, CalibrationProgress } from '@/core/layout-calibration'
import { performanceDiagnostics } from '@/services/performance-diagnostics'
import { samplingGapBudgetMs } from '@/core/source-continuity'
import type { SessionSamplingStage } from '@/services/analysis-pipeline'
import { computed, markRaw, onScopeDispose, ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import { ElMessage } from 'element-plus'
import { cloneCaptureProfile } from '@/core/capture-profile'
import { calibrationRegionsChanged } from '@/core/layout-calibration'
import {
  evaluateSessionStartBaseline,
  SessionStartExpConsensus,
  type ConfirmedSessionStartExp,
  type SessionStartIssue,
  type SessionStartBaselineEvaluation,
  type SessionStartBaselineStatus,
} from '@/core/session-start-baseline'
import { SessionEngine } from '@/core/session-engine'
import { rankSourceRestriction } from '@/core/rank-capture'
import { ExpAdmissionFilter, type ExpAdmissionCheckpoint } from '@/core/exp-admission'
import {
  DEFAULT_CAPTURE_FPS,
  METRIC_RECOGNITION_INTERVAL_VERSION,
  metricRecognitionEnabled,
  migrateRecognitionFrequencies,
  recommendedMetricRecognitionIntervalsMs,
} from '@/core/recognition-settings'
import { recognitionMetricKeys, type CaptureProfile, type ExpConfirmationFailureReason, type GameStateSnapshot, type LiveRateSnapshot, type MetricKey, type MetricRecognitionFailureReason, type RawReading, type RecognitionMetricKey, type SessionJourney, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import { i18n } from '@/i18n'
import { PresetRegistry } from '@/presets/registry'
import type { GamePreset, HudLocation, PresetRecognizer } from '@/presets/types'
import { AnalysisPipeline, type PipelineResult } from '@/services/analysis-pipeline'
import {
  captureService,
  classifyCaptureFailure,
  CaptureSelectionReplacedError,
  createCaptureDiagnostic,
  type CaptureDiagnostic,
  type CaptureFailurePhase,
  type CaptureFailureKind,
  type CaptureInfo,
  type CaptureMediaState,
  type CaptureSelectionPreference,
  type CaptureStatus,
} from '@/services/capture-service'
import { createRankQueueItems, notifyRankQueueChanged } from '@/services/rank-api'
import { db, RecoveryBuffer, saveEndedSessionState, saveSessionState } from '@/services/database'
import {
  ensureOcrModelsCached,
  isOcrResourceLoadError,
  type OcrCachePreparationResult,
  type OcrCacheProgress,
} from '@/services/ocr-resource-cache'
import { releaseSharedOcrEngine } from '@/services/ocr-engine'
import { OcrInitializationError, type OcrStartupDiagnostic } from '@/services/ocr-startup'
import { pipService, type PipData } from '@/services/pip-service'
import { classifyPipError, createPipIssue, getPipAvailability, PipOpenError, type PipIssue, type PipIssueCode, type PipOpenResult } from '@/services/pip-availability'
import { ocrRuntimeBlock, probeIndexedDb } from '@/services/browser-capabilities'
import { finalSamplingBudgetMs, performanceConfigs, recognitionForTier } from '@/core/performance-settings'
import { useAppStore } from './app'
import { reloadSafety, saveBeforeReload } from '@/services/reload-safety'
import { InitialLayoutRecognition, withInitialLayoutLock } from '@/services/initial-layout-recognition'

export type OcrStatus = 'idle' | 'checking' | 'downloading' | 'prepared' | 'initializing' | 'ready' | 'error'
export type OcrStage = 'idle' | 'checking' | 'detection' | 'recognition' | 'prepared' | 'initializing' | 'ready'
export type OcrErrorType = 'resource' | 'initialization' | 'unknown' | 'simd' | 'insecure-context'
export type CaptureStartupPhase = 'idle' | 'picker' | 'selected' | 'resources' | 'worker' | 'video' | 'ready' | 'error'
export interface SessionStartProgress {
  readonly phase: 'confirming' | 'slow'
  readonly stage?: SessionSamplingStage
  readonly slowReason?: 'frame' | 'ocr'
  readonly requestedAt: number
  readonly elapsedMs: number
  readonly confirmedSamples: 0 | 1 | 2
  readonly targetSamples: 2
  readonly currentMetric: 'level' | 'exp'
  readonly candidate?: number
  readonly reviewFailureReason?: MetricRecognitionFailureReason
  readonly confirmationFailureReason?: ExpConfirmationFailureReason
  readonly lastInferenceDurationMs?: number
}

const BACKGROUND_CONTINUITY_GAP_MS = 5_000
const SESSION_START_BASELINE_MAX_AGE_MS = 2_000
const SESSION_START_BASELINE_SOFT_WAIT_MS = 6_000
const SESSION_START_BASELINE_HARD_TIMEOUT_MS = 24_000
const SESSION_START_BASELINE_ABSOLUTE_TIMEOUT_MS = 60_000
const SESSION_START_RETRY_DELAY_MS = 250
const SLOW_CAPTURE_SELECTION_MS = 15_000
const CAPTURE_STARTUP_PHASE_KEY = 'maple-log:capture-startup-phase'

type SessionStartBaselineResult =
  | { readonly status: 'ready'; readonly baseline: GameStateSnapshot; readonly startedAt: number; readonly requestedAt: number }
  | { readonly status: Exclude<SessionStartBaselineStatus, 'ready'>; readonly baseline: null; readonly requestedAt: number; readonly hardTimedOut?: boolean; readonly confirmedSamples?: 0 | 1; readonly levelCandidate?: number; readonly reviewFailureReason?: MetricRecognitionFailureReason; readonly confirmationFailureReason?: ExpConfirmationFailureReason }
  | { readonly status: 'cancelled'; readonly baseline: null; readonly requestedAt: number }

export const useLiveStore = defineStore('live', () => {
  const app = useAppStore()
  const preset = shallowRef<GamePreset | null>(null)
  const profile = shallowRef<CaptureProfile | null>(null)
  const session = shallowRef<SessionRecord | null>(null)
  const liveRates = shallowRef<LiveRateSnapshot | null>(null)
  let expReviewRequest: string | null = null
  watch([() => session.value?.id, liveRates], ([sessionId, rates]) => {
    const readout = rates?.expReadout
    if (!sessionId || !readout) { expReviewRequest = null; return }
    performanceDiagnostics.observeExpReadout(sessionId, readout)
    const needsConfirmation = readout.status !== 'ready' && readout.reason !== 'source-unreadable'
    if (!needsConfirmation) { expReviewRequest = null; return }
    const key = `${sessionId}:${readout.scope}:${readout.since}`
    if (session.value?.status === 'running' && key !== expReviewRequest) {
      expReviewRequest = key
      pipeline?.requestExpConfirmation?.()
    }
  })
  const gameState = shallowRef<GameStateSnapshot | null>(null)
  const rawReading = shallowRef<RawReading | null>(null)
  const hudLocation = shallowRef<HudLocation | null>(null)
  const captureStatus = ref<CaptureStatus>(captureService.status)
  const captureLabel = ref('')
  const captureFailure = ref<CaptureFailureKind | null>(null)
  const captureDiagnostic = shallowRef<CaptureDiagnostic | null>(null)
  const capturePreference = ref<CaptureSelectionPreference>('window')
  const captureSelectionPending = ref(false)
  const captureStartupPhase = ref<CaptureStartupPhase>('idle')
  const error = ref<string | null>(null)
  const pipAvailability = shallowRef(getPipAvailability())
  const pipSupported = computed(() => pipAvailability.value.supported)
  const pipIssue = shallowRef<PipIssue | null>(null)
  const isOpeningPip = ref(false)
  let pipOpenPromise: Promise<PipOpenResult> | null = null
  let pipSource: 'live' | 'stream' = 'stream'
  const needsCalibration = ref(false)
  const isInitializingCalibration = ref(false)
  const firstUseCalibrationPhase = ref<'idle' | 'waiting' | 'running' | 'result'>('idle')
  const firstUseCalibrationOpen = ref(false)
  const firstUseCalibrationProgress = shallowRef<CalibrationProgress>({ stage: 'preparing', located: [], confirming: [], saved: [] })
  const firstUseCalibrationOutcome = ref<'' | 'complete' | 'partial' | 'failed' | 'saveFailed' | 'cancelled'>('')
  const firstUseCalibrationFrame = shallowRef<ImageData | null>(null)
  const firstUseCalibrationRetained = ref<RecognitionMetricKey[]>([])
  const firstUseCalibrationBlocking = computed(() => firstUseCalibrationPhase.value !== 'idle')
  const firstUseCalibrationBusy = computed(() => firstUseCalibrationPhase.value === 'waiting' || firstUseCalibrationPhase.value === 'running')
  let firstUseController: AbortController | null = null
  let firstUseOperation = 0
  let firstUseStorageWarningShown = false
  const firstUseHistory = new InitialLayoutRecognition(() => {
    if (firstUseStorageWarningShown) return
    firstUseStorageWarningShown = true
    ElMessage({ type: 'warning', message: String(i18n.global.t('firstUseCalibration.storageFailed')), duration: 6_000, showClose: true })
  })
  const ocrStatus = ref<OcrStatus>('idle')
  const ocrStage = ref<OcrStage>('idle')
  const ocrProgress = ref(0)
  const ocrCachedModels = ref(0)
  const ocrError = ref<string | null>(null)
  const ocrErrorType = ref<OcrErrorType | null>(null)
  const ocrStartupDiagnostic = shallowRef<OcrStartupDiagnostic | null>(null)
  const ocrCacheDegraded = ref(false)
  const isStarting = ref(false)
  const isEnding = ref(false)
  const pendingFinalization = shallowRef<Parameters<typeof saveEndedSessionState> | null>(null)
  const hasPendingFinalization = computed(() => pendingFinalization.value !== null)
  const isPreparingForReload = computed(() => reloadSafety.preparing)
  const startIssue = shallowRef<SessionStartIssue | null>(null)
  const sessionStartProgress = shallowRef<SessionStartProgress | null>(null)
  const journey = shallowRef<SessionJourney | null>(null)
  const previewRevision = ref(0)
  const onPreviewChanged = () => { previewRevision.value += 1 }
  captureService.addEventListener('preview', onPreviewChanged)
  const video = computed(() => {
    previewRevision.value
    return captureService.video
  })
  const recovery = markRaw(new RecoveryBuffer())
  let pipeline: AnalysisPipeline | null = null
  let sessionEngine: SessionEngine | null = null
  let sessionExpCheckpoint: ExpAdmissionCheckpoint | null = null
  let calibrationMetric: RecognitionMetricKey | null = null
  let captureGeneration = 0
  let ocrResourcePreparation: Promise<void> | null = null
  let ocrEngineInitialization: Promise<void> | null = null
  let retainedOcrPreparation: OcrCachePreparationResult | null = null
  let endSessionPromise: Promise<void> | null = null
  let startSessionPromise: Promise<void> | null = null
  let startBaselineController: AbortController | null = null
  let settlementController: AbortController | null = null
  let liveRateRefreshTimer = 0
  let lastLiveRateRefreshAt = 0
  let pageHiddenAt: number | null = null
  let pageWasFrozen = false
  let mediaContinuityLost = false
  let mediaWaitingTimer: ReturnType<typeof setTimeout> | null = null
  let recoveringPipeline = false
  let activePipelineGeneration = -1
  let ocrLifecycleRevision = 0
  let preparedOcrFingerprint = ''
  let readyOcrFingerprint = ''
  let cacheDegradedWarningShown = false
  let latestObservation: ValidatedObservation | null = null
  let latestObservationGeneration = -1
  let initialized = false
  let disposed = false
  let cancelCalibrationWait: (() => void) | null = null
  let cancelOcrProgress: (() => void) | null = null
  const releaseStoreDiagnostic = performanceDiagnostics.acquire('live-stores')
  const stopLongTaskObserver = performanceDiagnostics.observeLongTasks()

  const isCapturing = computed(() => captureStatus.value === 'active')
  const isSelectingWindow = computed(() => captureSelectionPending.value || captureStatus.value === 'requesting'
    || captureStatus.value === 'connecting'
    || (captureStatus.value === 'selected' && ocrStatus.value !== 'error'))
  const captureCompatibilityIssue = computed(() => captureService.compatibilityIssue)
  const captureSupported = computed(() => captureCompatibilityIssue.value === null)
  const canSelectWindow = computed(() => Boolean(preset.value && profile.value && captureSupported.value))
  const isRunning = computed(() => session.value?.status === 'running')
  const isPaused = computed(() => session.value?.status === 'paused')

  watch([captureStatus, () => profile.value?.id, () => reloadSafety.preparing], ([status, id, reloading], previous) => {
    if (status !== 'active' || reloading || previous && id !== previous[1]) invalidateFirstUseCalibration()
  }, { flush: 'sync' })
  onScopeDispose(() => {
    disposed = true
    releaseStoreDiagnostic()
    stopLongTaskObserver()
    captureService.removeEventListener('preview', onPreviewChanged)
    captureService.removeEventListener('status', onCaptureStatus)
    captureService.removeEventListener('rank-assessment', restrictSessionRankForSource)
    captureService.removeEventListener('ended', onCaptureEnded)
    captureService.removeEventListener('media-state', onCaptureMediaState)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    document.removeEventListener('freeze', handlePageFreeze)
    document.removeEventListener('resume', handlePageResume)
    window.removeEventListener('pageshow', handlePageResume)
    invalidateFirstUseCalibration()
    cancelCalibrationWait?.()
    cancelOcrProgress?.()
    startBaselineController?.abort()
    settlementController?.abort()
    stopLiveRateRefresh()
    if (mediaWaitingTimer !== null) clearTimeout(mediaWaitingTimer)
    mediaWaitingTimer = null
    // Only the Pinia scope owns these resources. Route components must not stop them.
    if (sessionEngine && session.value?.status === 'running') {
      session.value = sessionEngine.interrupt(Date.now())
      journey.value = sessionEngine.journey()
      void persistSession().catch(() => undefined)
    }
    void releaseRecognitionForCaptureSelection().catch(() => undefined)
    pipService.close()
    captureService.stop()
    sessionEngine = null
    latestObservation = null
    rawReading.value = null
  })

  watch(() => app.pipSkin, () => pipService.refresh())
  const appliedPerformanceTier = ref(app.performanceSettings.tier)
  const captureFpsApplied = ref<number | null>(null)
  const performanceChangeDeferred = computed(() => firstUseCalibrationBusy.value || isStarting.value || isEnding.value || hasPendingFinalization.value)
  const captureFrequencyPending = computed(() => captureFpsApplied.value !== null
    && ['active', 'selected', 'connecting'].includes(captureStatus.value)
    && captureFpsApplied.value !== app.performanceConfig.captureFpsLimit)
  watch([() => app.performanceSettings.tier, performanceChangeDeferred], ([tier, deferred]) => {
    if (deferred || tier === appliedPerformanceTier.value) return
    appliedPerformanceTier.value = tier
    restrictSessionRankForPerformance()
    pipeline?.setProcessingFrameRateLimit?.(performanceConfigs[tier].processingFpsLimit)
    if (profile.value) void updateProfile(profile.value).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }, { flush: 'sync' })
  watch(() => app.pipStream, () => pipService.refresh(), { deep: true })

  const latestPipLevelUp = computed(() => {
    const current = journey.value
    if (!current) return undefined
    for (let index = current.events.length - 1; index >= 0; index--) {
      const event = current.events[index]!
      if (event.type === 'level-up') return `${current.sessionId}:${event.capturedAt}:${event.fromLevel}:${event.toLevel}`
    }
    return undefined
  })

  const onCaptureStatus: EventListener = event => {
    captureStatus.value = (event as CustomEvent<CaptureStatus>).detail
    if (captureStatus.value === 'active') restrictSessionRankForSource()
  }
  const onCaptureEnded = () => {
    void interruptCapture().catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }
  const onCaptureMediaState: EventListener = event => {
    handleCaptureMediaState((event as CustomEvent<CaptureMediaState>).detail)
  }

  async function initialize(): Promise<void> {
    if (initialized || disposed) return
    initialized = true
    try {
      await loadPreset(app.presetId, false)
      if (disposed) return
      captureService.addEventListener('status', onCaptureStatus)
      captureService.addEventListener('rank-assessment', restrictSessionRankForSource)
      captureService.addEventListener('ended', onCaptureEnded)
      captureService.addEventListener('media-state', onCaptureMediaState)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('freeze', handlePageFreeze)
      document.addEventListener('resume', handlePageResume)
      window.addEventListener('pageshow', handlePageResume)
    } catch (caught) {
      initialized = false
      error.value = !probeIndexedDb()
        ? String(i18n.global.t('capability.storage.hint'))
        : caught instanceof Error ? `${caught.name}: ${caught.message}` : String(caught)
      ocrStatus.value = 'error'
      ocrError.value = error.value
      ocrErrorType.value = 'unknown'
      return
    }
  }

  async function loadPreset(id: string, initializeResources = initialized): Promise<void> {
    if (disposed || reloadSafety.preparing || pendingFinalization.value) return
    startBaselineController?.abort()
    if (session.value && session.value.status !== 'ended' && preset.value?.id !== id) {
      throw new Error('End the active record before changing presets')
    }
    invalidateFirstUseCalibration()
    await firstUseHistory.initialize()
    if (disposed) return
    const previousPreset = preset.value
    const previousPipeline = pipeline
    ocrLifecycleRevision += 1
    ocrResourcePreparation = null
    ocrEngineInitialization = null
    previousPipeline?.stop()
    const engineRelease = previousPreset
      ? releaseSharedOcrEngine(previousPreset.remoteResources.ocrModels)
      : Promise.resolve()
    previousPipeline?.dispose()
    pipeline = null
    activePipelineGeneration = -1
    releasePreparedOcrResources()
    preparedOcrFingerprint = ''
    readyOcrFingerprint = ''
    setOcrIdle()
    latestObservation = null
    latestObservationGeneration = -1
    stopLiveRateRefresh()
    await engineRelease
    if (disposed) return
    const loadedPreset = await PresetRegistry.load(id)
    if (disposed) return
    preset.value = loadedPreset
    app.presetId = id
    sessionEngine = new SessionEngine(preset.value)
    journey.value = null
    liveRates.value = null
    const exactProfiles = await db.profiles.where('[presetId+presetVersion]').equals([id, preset.value.version]).toArray()
    const exact = exactProfiles.reduce<CaptureProfile | null>((latest, item) => !latest || item.updatedAt > latest.updatedAt ? item : latest, null)
    const previous = exact ? null : await db.profiles.orderBy('updatedAt').reverse().filter((item) => item.presetId === id).first()
    if (disposed) return
    const migrated = previous ? preset.value.migrateProfile(previous) : null
    needsCalibration.value = Boolean(previous && !migrated)
    const selectedProfile = exact ? preset.value.migrateProfile(exact) ?? createProfile(preset.value) : migrated ?? createProfile(preset.value)
    profile.value = {
      ...selectedProfile,
      recognition: recognitionForTier(migrateRecognitionFrequencies(selectedProfile.recognition), appliedPerformanceTier.value),
    }
    await db.profiles.put(profile.value)
    if (initializeResources && isCapturing.value) await initializeOcr()
  }

  async function selectWindow(preference: CaptureSelectionPreference = 'window'): Promise<void> {
    if (disposed || reloadSafety.preparing || pendingFinalization.value) return
    if (isEnding.value || isSelectingWindow.value) return
    if (!canSelectWindow.value || !preset.value || !profile.value) return
    invalidateFirstUseCalibration()
    captureSelectionPending.value = true
    capturePreference.value = preference
    const selectionStartedAt = Date.now()
    const selection = captureService.selectWindow(preference, app.performanceConfig.captureFpsLimit)
    captureFpsApplied.value = app.performanceConfig.captureFpsLimit
    const selectionId = captureService.selectionId
    setCaptureStartupPhase('picker')
    startBaselineController?.abort()
    sessionStartProgress.value = null
    startIssue.value = null
    error.value = null
    captureFailure.value = null
    captureDiagnostic.value = null
    captureGeneration += 1
    const interrupted = session.value?.status === 'running'
      ? interruptCapture().catch((caught: unknown) => {
          error.value = caught instanceof Error ? caught.message : String(caught)
        })
      : Promise.resolve()
    const engineRelease = releaseRecognitionForCaptureSelection()
    try {
      const info = await selection
      if (selectionId !== captureService.selectionId) return
      const selectionDurationMs = Date.now() - selectionStartedAt
      setCaptureStartupPhase('video')
      let connected = info
      let connectedFromSelection = false
      if (captureStatus.value === 'selected' || captureService.status === 'selected') {
        try {
          connected = await captureService.connectSelectedWindow()
          connectedFromSelection = true
        } catch (caught) {
          if (selectionId !== captureService.selectionId || caught instanceof CaptureSelectionReplacedError) return
          setCaptureStartupPhase('error')
          reportCaptureFailure(caught, 'track')
          return
        }
        if (selectionId !== captureService.selectionId) return
      }
      await applyCaptureInfo(connected, selectionId)
      if (selectionId !== captureService.selectionId) return
      if (connectedFromSelection) await waitForNativePickerExit()
      await interrupted
      await engineRelease
      if (selectionId !== captureService.selectionId) return
      captureFailure.value = null
      mediaContinuityLost = false
      pageWasFrozen = false
      pageHiddenAt = null
      if (selectionDurationMs >= SLOW_CAPTURE_SELECTION_MS) {
        ElMessage({
          type: 'warning',
          message: String(i18n.global.t('live.captureSelectionSlow')),
          duration: 6_000,
          showClose: true,
        })
      }
      await initializeOcr()
    } catch (caught) {
      if (selectionId !== captureService.selectionId || caught instanceof CaptureSelectionReplacedError) return
      setCaptureStartupPhase(captureService.status === 'selected' ? 'error' : 'idle')
      reportCaptureFailure(caught, 'selection')
    } finally {
      captureSelectionPending.value = false
    }
  }

  function reportCaptureFailure(caught: unknown, phase: CaptureFailurePhase): void {
    const failure = classifyCaptureFailure(caught)
    captureFailure.value = failure
    captureDiagnostic.value = captureService.lastDiagnostic ?? createCaptureDiagnostic(caught, phase, capturePreference.value)
    const messageKey = failure === 'permission-denied' ? 'live.capturePermissionDenied'
      : ['security-restricted', 'invalid-state', 'insecure-context', 'policy-blocked'].includes(failure) ? `captureRecovery.${failure}Hint`
      : failure === 'unsupported' ? 'live.captureUnsupported'
        : failure === 'timeout' ? 'live.captureTimeout' : 'live.captureSourceUnavailable'
    if (failure === 'permission-denied') {
      ElMessage({
        type: 'warning',
        message: String(i18n.global.t(messageKey)),
        duration: 4_500,
        showClose: true,
      })
    }
  }

  function startSession(): Promise<void> {
    if (disposed || reloadSafety.preparing || pendingFinalization.value) return Promise.resolve()
    if (startSessionPromise) return startSessionPromise
    if (isEnding.value || (session.value && session.value.status !== 'ended')) return Promise.resolve()
    startSessionPromise = (async () => {
      if (!isCapturing.value) await selectWindow()
      if (firstUseCalibrationBlocking.value) {
        const selectionId = captureService.selectionId
        await new Promise<void>(resolve => {
          const finish = () => { stop(); cancelCalibrationWait = null; resolve() }
          const stop = watch(firstUseCalibrationBlocking, blocked => { if (!blocked) finish() })
          cancelCalibrationWait = finish
        })
        if (disposed || selectionId !== captureService.selectionId || firstUseHistory.state === 'pending') return
      }
      if (reloadSafety.preparing || pendingFinalization.value) return
      if (!isCapturing.value || !profile.value || !sessionEngine) return
      isStarting.value = true

      const baselineResult = await acquireSessionStartBaseline()
      if (disposed || baselineResult.status === 'cancelled') return
      if (baselineResult.status !== 'ready') {
        startIssue.value = sessionStartIssue(
          baselineResult.status,
          baselineResult.requestedAt,
          baselineResult.hardTimedOut ?? false,
          baselineResult.confirmedSamples,
          baselineResult.levelCandidate,
          baselineResult.reviewFailureReason,
          baselineResult.confirmationFailureReason,
        )
        return
      }

      const { baseline, startedAt } = baselineResult
      startIssue.value = null
      captureGeneration += 1
      session.value = sessionEngine.start(profile.value.id, startedAt, baseline, captureGeneration)
      restrictSessionRankForPerformance()
      restrictSessionRankForSource()
      const initialExpAdmission = new ExpAdmissionFilter()
      initialExpAdmission.reset(baseline)
      sessionExpCheckpoint = initialExpAdmission.checkpoint()
      journey.value = sessionEngine.journey()
      liveRates.value = sessionEngine.liveRates(startedAt)
      startLiveRateRefresh()
      await saveSessionState(session.value, journey.value)
      if (pipeline) {
        activePipelineGeneration = captureGeneration
        await pipeline.beginSession(captureService.video, session.value.id, baseline, captureGeneration, startedAt)
      } else {
        startPipeline(session.value.id, baseline, captureGeneration)
      }
    })().finally(() => {
      isStarting.value = false
      startSessionPromise = null
    })
    return startSessionPromise
  }

  async function pauseSession(): Promise<void> {
    if (reloadSafety.preparing || pendingFinalization.value) return
    if (!sessionEngine || isEnding.value) return
    const pausedAt = Date.now()
    session.value = sessionEngine.pause(pausedAt)
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(pausedAt)
    stopLiveRateRefresh()
    await saveSessionState(session.value, journey.value)
  }

  async function resumeSession(): Promise<void> {
    if (reloadSafety.preparing || pendingFinalization.value) return
    if (!sessionEngine || isEnding.value) return
    const resumedAt = Date.now()
    captureGeneration += 1
    session.value = sessionEngine.resume(resumedAt, captureGeneration)
    journey.value = sessionEngine.journey()
    pipeline?.setCaptureGeneration(captureGeneration)
    activePipelineGeneration = pipeline ? captureGeneration : -1
    pipeline?.resetBaseline(undefined, sessionExpCheckpoint ?? undefined)
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    startLiveRateRefresh()
    if (session.value) await saveSessionState(session.value, journey.value)
  }

  function endSession(): Promise<void> {
    if (endSessionPromise) return endSessionPromise
    if (!pendingFinalization.value && (!sessionEngine || !session.value || session.value.status === 'ended')) return Promise.resolve()
    const engine = sessionEngine
    endSessionPromise = (async () => {
      isEnding.value = true
      if (!pendingFinalization.value && engine) {
        if (session.value?.status === 'running' && isCapturing.value && pipeline) {
          settlementController = new AbortController()
          let timeoutId = 0
          const timeout = new Promise<boolean>((resolve) => {
            timeoutId = window.setTimeout(() => {
              settlementController?.abort()
              resolve(false)
            }, finalSamplingBudgetMs(captureIntervalMs()))
          })
          try {
            const completed = await Promise.race([
              pipeline.settle(captureService.video, ['level', 'exp'], settlementController.signal),
              timeout,
            ])
            if (!completed) ElMessage.warning(i18n.global.t('performance.finalSampleIncomplete'))
          } catch {
            // Keep all previously confirmed totals if the final sample fails.
            ElMessage.warning(i18n.global.t('performance.finalSampleIncomplete'))
          } finally {
            window.clearTimeout(timeoutId)
            settlementController.abort()
            settlementController = null
          }
        }

        const endedAt = Date.now()
        stopLiveRateRefresh()
        session.value = engine.end(endedAt)
        journey.value = engine.journey()
        liveRates.value = engine.liveRates(endedAt)
        const rankItems = createRankQueueItems(session.value.id, engine.rankSubmissions())
        pendingFinalization.value = [session.value, journey.value, rankItems]
      }
      const finalization = pendingFinalization.value
      if (!finalization) return
      await saveEndedSessionState(...finalization)
      pendingFinalization.value = null
      if (finalization[2].length) notifyRankQueueChanged()
    })().finally(() => {
      isEnding.value = false
      endSessionPromise = null
    })
    return endSessionPromise
  }

  async function updateProfile(next: CaptureProfile): Promise<void> {
    const previous = profile.value
    const sourceChanged = previous ? captureSourceChanged(previous, next) : true
    const adaptiveMetricToReset = previous ? changedAdaptiveMetric(previous, next) : null
    const onlyRegionsChanged = preset.value?.layoutVersion && previous && previous.sourceWidth === next.sourceWidth && previous.sourceHeight === next.sourceHeight
      && JSON.stringify(previous.colorCalibration) === JSON.stringify(next.colorCalibration)
      && JSON.stringify(previous.recognition) === JSON.stringify(next.recognition)
      && calibrationRegionsChanged(previous, next).length > 0
    profile.value = { ...cloneCaptureProfile(next), recognition: recognitionForTier(next.recognition, appliedPerformanceTier.value), updatedAt: Date.now() }
    pipService.refresh()
    needsCalibration.value = false
    pipeline?.updateProfile(profile.value)
    if (sourceChanged && !onlyRegionsChanged) {
      startBaselineController?.abort()
      captureGeneration += 1
      pipeline?.setCaptureGeneration(captureGeneration, adaptiveMetricToReset ?? undefined)
      activePipelineGeneration = pipeline ? captureGeneration : -1
      pipeline?.resetBaseline(adaptiveMetricToReset ?? undefined, session.value?.status !== 'ended' ? sessionExpCheckpoint ?? undefined : undefined)
    }
    const snapshot = profile.value
    await saveBeforeReload(`profile:${snapshot.id}`, async () => { await db.profiles.put(snapshot) })
  }

  function setCalibrationMetric(metric: RecognitionMetricKey | null): void {
    calibrationMetric = metric
    pipeline?.setDiagnosticMetric(metric)
  }

  async function initializeCalibration(options: CalibrationInitializationOptions = {}): Promise<RecognitionMetricKey[]> {
    if (firstUseCalibrationBlocking.value) throw new Error('First-use calibration is in progress')
    return performCalibration(options)
  }

  async function performCalibration(options: CalibrationInitializationOptions = {}): Promise<RecognitionMetricKey[]> {
    if (!pipeline || !isCapturing.value || isInitializingCalibration.value || isStarting.value || isEnding.value || reloadSafety.preparing) throw new Error('Calibration is unavailable')
    isInitializingCalibration.value = true
    try { return await pipeline.initializeLayout(captureService.video, options) }
    finally { isInitializingCalibration.value = false }
  }

  function invalidateFirstUseCalibration(): void {
    if (!firstUseCalibrationBlocking.value) return
    firstUseOperation += 1
    firstUseController?.abort()
    firstUseController = null
    if (firstUseCalibrationBusy.value) pipeline?.stop()
    firstUseCalibrationOpen.value = false
    firstUseCalibrationPhase.value = 'idle'
    firstUseCalibrationFrame.value = null
  }

  function skipFirstUseCalibration(): void {
    if (firstUseCalibrationProgress.value.stage !== 'saving') firstUseController?.abort('user-skip')
  }

  function closeFirstUseCalibration(): void {
    if (firstUseCalibrationBusy.value) return
    firstUseOperation += 1
    firstUseCalibrationOpen.value = false
    firstUseCalibrationPhase.value = 'idle'
    firstUseCalibrationFrame.value = null
  }

  async function runFirstUseCalibration(metrics: readonly RecognitionMetricKey[] = recognitionMetricKeys, action: CalibrationInitializationOptions['action'] = 'locate'): Promise<void> {
    if (!['waiting', 'result'].includes(firstUseCalibrationPhase.value) || !pipeline || !profile.value || !isCapturing.value || ocrStatus.value !== 'ready' || reloadSafety.preparing) return
    if (captureService.video.videoWidth !== profile.value.sourceWidth || captureService.video.videoHeight !== profile.value.sourceHeight) {
      // The frame may resize while the startup dialog is still leaving.
      invalidateFirstUseCalibration()
      pipeline.resume(captureService.video)
      return
    }
    const initial = firstUseCalibrationPhase.value === 'waiting'
    const currentPipeline = pipeline, source = captureService.video.srcObject, selectionId = captureService.selectionId
    const profileId = profile.value.id, width = captureService.video.videoWidth, height = captureService.video.videoHeight, generation = captureGeneration
    const operation = ++firstUseOperation
    const controller = new AbortController()
    firstUseController = controller
    const isCurrent = () => operation === firstUseOperation && pipeline === currentPipeline && isCapturing.value
      && captureService.video.srcObject === source && captureService.selectionId === selectionId && profile.value?.id === profileId
      && captureService.video.videoWidth === width && captureService.video.videoHeight === height && captureGeneration === generation && !reloadSafety.preparing
    const previous = initial ? { located: [], saved: [], failed: [] } : firstUseCalibrationProgress.value
    const keep = (items: readonly RecognitionMetricKey[]) => items.filter(metric => !metrics.includes(metric))
    const merge = (progress: CalibrationProgress): CalibrationProgress => ({ ...progress, metrics,
      located: [...keep(previous.located), ...progress.located], saved: [...keep(previous.saved), ...progress.saved],
      failed: [...keep(previous.failed ?? []), ...(progress.failed ?? [])] })
    firstUseCalibrationPhase.value = 'running'
    firstUseCalibrationOutcome.value = ''
    firstUseCalibrationFrame.value = null
    firstUseCalibrationProgress.value = merge({ stage: 'preparing', located: [], confirming: [], saved: [] })
    firstUseCalibrationRetained.value = recognitionMetricKeys.filter(metric => profile.value?.roiOverrides[metric] || profile.value?.autoRois?.[metric])
    const recognize = async () => {
      if (initial) await firstUseHistory.refresh()
      if (!isCurrent()) return
      if (initial && firstUseHistory.state === 'completed') {
        firstUseCalibrationPhase.value = 'idle'
        currentPipeline.resume(captureService.video)
        return
      }
      firstUseCalibrationOpen.value = true
      try {
        const completed = await performCalibration({ metrics, action, signal: controller.signal, isCurrent,
          onFrame: frame => { if (isCurrent() && !controller.signal.aborted) firstUseCalibrationFrame.value = frame },
          onProgress: progress => { if (isCurrent() && !controller.signal.aborted) firstUseCalibrationProgress.value = merge(progress) },
        })
        if (!isCurrent()) return
        firstUseCalibrationProgress.value = merge({ ...firstUseCalibrationProgress.value, stage: 'ended', located: completed, confirming: [], saved: completed, failed: metrics.filter(metric => !completed.includes(metric)) })
        firstUseCalibrationOutcome.value = firstUseCalibrationProgress.value.saved.length === recognitionMetricKeys.length ? 'complete' : 'partial'
      } catch {
        if (!isCurrent()) return
        firstUseCalibrationOutcome.value = controller.signal.reason === 'user-skip' ? 'cancelled'
          : firstUseCalibrationProgress.value.stage === 'saving' ? 'saveFailed' : 'failed'
      }
      if (!isCurrent()) return
      await firstUseHistory.complete(isCurrent)
      if (!isCurrent()) return
      firstUseCalibrationProgress.value = { ...firstUseCalibrationProgress.value, stage: 'ended' }
      firstUseCalibrationPhase.value = 'result'
    }
    try {
      if (initial) await withInitialLayoutLock(recognize, controller.signal)
      else await recognize()
    } finally {
      if (operation === firstUseOperation) {
        firstUseController = null
        if (!isCurrent()) {
          // A resize can invalidate a task without changing reactive capture status.
          invalidateFirstUseCalibration()
          if (pipeline === currentPipeline && isCapturing.value && !reloadSafety.preparing) currentPipeline.resume(captureService.video)
        }
      }
    }
  }

  async function loadCalibrationSize(width: number, height: number): Promise<CaptureProfile> {
    const current = profile.value
    const gamePreset = preset.value
    if (!current || !gamePreset) throw new Error('No capture profile')
    if (current.sourceWidth === width && current.sourceHeight === height) return current
    const profiles = await db.profiles.where('[presetId+presetVersion]').equals([current.presetId, current.presetVersion]).toArray()
    const matching = profiles.filter(item => item.layoutVersion === gamePreset.layoutVersion && item.sourceWidth === width && item.sourceHeight === height)
      .sort((a, b) => b.updatedAt - a.updatedAt)[0]
    const next = matching ? { ...matching, recognition: current.recognition } : {
      ...cloneCaptureProfile(current), id: crypto.randomUUID(), sourceWidth: width, sourceHeight: height,
      autoRois: {}, roiOverrides: {}, colorCalibration: {}, hudRect: gamePreset.hud.fallback, updatedAt: Date.now(),
    }
    await db.profiles.put(next)
    if (profile.value !== current) throw new Error('Capture profile changed')
    profile.value = next
    return next
  }

  async function saveCalibrationProfile(next: CaptureProfile, previous: CaptureProfile, isCurrent?: () => boolean): Promise<CaptureProfile> {
    if (isCurrent && !isCurrent()) throw new Error('Calibration cancelled')
    const current = profile.value
    if (!current || current.id !== previous.id || current.updatedAt !== previous.updatedAt) throw new Error('Capture profile changed')
    const savingPipeline = pipeline
    const saved = cloneCaptureProfile(next)
    const canSave = () => profile.value === current && pipeline === savingPipeline && (!isCurrent || isCurrent())
    await saveBeforeReload(`profile:${saved.id}`, async () => {
      // A stale queued save must also become a no-op when retried before reload.
      if (!canSave()) return
      if (isCurrent) {
        await db.transaction('rw', db.profiles, async () => {
          if (!canSave()) throw new Error('Capture profile changed')
          await db.profiles.put(saved)
          if (!canSave()) throw new Error('Capture profile changed')
        })
      } else await db.profiles.put(saved)
    })
    if (profile.value !== current || pipeline !== savingPipeline || isCurrent && !isCurrent()) throw new Error('Capture profile changed')
    profile.value = saved
    needsCalibration.value = false
    pipService.refresh()
    return saved
  }

  const pipData = computed<PipData>(() => ({
    mapRecognitionState: rawReading.value?.diagnostics.metrics?.mapName?.mapConfirmation?.state,
    skin: app.pipSkin,
    streamOptions: app.pipStream,
    levelUpId: latestPipLevelUp.value,
    session: session.value,
    state: gameState.value,
    liveRates: liveRates.value,
    canStart: isCapturing.value && ocrStatus.value === 'ready' && !firstUseCalibrationBlocking.value && !isStarting.value && !isPreparingForReload.value && !hasPendingFinalization.value,
    isStarting: isStarting.value,
    isEnding: isEnding.value,
    isPreparingForReload: isPreparingForReload.value || hasPendingFinalization.value,
    enabledMetrics: {
      mesos: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'mesos') : true,
      hp: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'hp') : true,
      mp: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'mp') : true,
      mapName: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'mapName') : true,
    },
  }))

  const pipDisabledReason = computed(() => isPreparingForReload.value ? 'reloading'
    : isEnding.value || hasPendingFinalization.value ? 'ending' : null)

  function pipPrerequisite(source: 'live' | 'stream'): PipIssueCode | null {
    if (pipDisabledReason.value) return pipDisabledReason.value
    if (source === 'stream') return null
    if (!isCapturing.value && !['selected', 'connecting'].includes(captureStatus.value)) return 'not-connected'
    if (ocrStatus.value !== 'ready') return ocrStatus.value === 'error' ? 'ocr-error' : 'ocr-preparing'
    return isCapturing.value ? null : 'not-connected'
  }

  function refreshPipAvailability(): void {
    pipAvailability.value = getPipAvailability()
  }

  function dismissPipIssue(): void { pipIssue.value = null }

  watch([captureStatus, ocrStatus, pipDisabledReason], () => {
    if (pipIssue.value?.stage !== 'prerequisite') return
    const code = pipPrerequisite(pipSource)
    pipIssue.value = code ? createPipIssue(new PipOpenError(code, 'prerequisite'), pipAvailability.value) : null
  })

  function openPip(source: 'live' | 'stream' = 'stream'): Promise<PipOpenResult> {
    if (disposed) return Promise.resolve({ status: 'cancelled' })
    if (pipOpenPromise) return pipOpenPromise
    pipSource = source
    refreshPipAvailability()
    const code = pipAvailability.value.reason ?? pipPrerequisite(source)
    if (code) {
      const issue = createPipIssue(new PipOpenError(code, pipAvailability.value.reason ? 'availability' : 'prerequisite'), pipAvailability.value)
      pipIssue.value = issue
      return Promise.resolve({ status: 'failed', issue })
    }
    pipIssue.value = null
    isOpeningPip.value = true
    const open = async (): Promise<PipOpenResult> => {
      try {
        const result = await pipService.open(() => pipData.value, {
          start: startSession, pause: pauseSession, resume: resumeSession, end: endSession,
          heartbeat: handlePipHeartbeat,
        }, app.locale)
        return { status: result === 'cancelled' ? 'cancelled' : 'opened' }
      } catch (error) {
        refreshPipAvailability()
        const issue = createPipIssue(classifyPipError(error, 'initialization'))
        pipIssue.value = issue
        return { status: 'failed', issue }
      } finally {
        isOpeningPip.value = false
      }
    }
    // Call immediately: deferring open to a .then() would lose user activation.
    const pending = open()
    pipOpenPromise = pending
    void pending.then(() => { if (pipOpenPromise === pending) pipOpenPromise = null })
    return pending
  }

  async function initializeOcr(): Promise<void> {
    if (disposed) return
    const selectionId = captureService.selectionId
    const hasSelectedSource = captureStatus.value === 'selected' || isCapturing.value
    if (!hasSelectedSource) {
      setOcrIdle()
      return
    }
    setCaptureStartupPhase('resources')
    await prepareOcrResources()
    if (selectionId !== captureService.selectionId) return
    if ((captureStatus.value !== 'selected' && !isCapturing.value) || ocrStatus.value === 'error') {
      if (ocrStatus.value === 'error') setCaptureStartupPhase('error')
      return
    }
    setCaptureStartupPhase('worker')
    if (captureStatus.value === 'selected') await yieldToMainThread()
    if (selectionId !== captureService.selectionId) return
    await initializeOcrEngine()
    if (selectionId !== captureService.selectionId) return
    if (ocrStatus.value !== 'ready') {
      if (ocrError.value) setCaptureStartupPhase('error')
      return
    }
    if (captureStatus.value === 'selected') {
      setCaptureStartupPhase('video')
      let info: CaptureInfo
      try {
        info = await captureService.connectSelectedWindow()
      } catch (caught) {
        if (selectionId !== captureService.selectionId || caught instanceof CaptureSelectionReplacedError) return
        setCaptureStartupPhase('error')
        // Capture recovery lives on the page, outside the blocking OCR dialog.
        setOcrIdle()
        reportCaptureFailure(caught, 'track')
        return
      }
      await applyCaptureInfo(info, selectionId)
      if (selectionId !== captureService.selectionId) return
    }
    if (selectionId !== captureService.selectionId) return
    setCaptureStartupPhase('ready')
    await activateCaptureRecognition()
  }

  async function applyCaptureInfo(info: CaptureInfo, selectionId: number): Promise<void> {
    if (selectionId !== captureService.selectionId) return
    captureLabel.value = info.label
    if (!profile.value) return
    if (preset.value?.locateLayout) await loadCalibrationSize(info.width || profile.value.sourceWidth, info.height || profile.value.sourceHeight)
    else profile.value = {
      ...profile.value,
      sourceWidth: info.width || profile.value.sourceWidth,
      sourceHeight: info.height || profile.value.sourceHeight,
      updatedAt: Date.now(),
    }
    try {
      await db.profiles.put(profile.value)
    } catch (caught) {
      // A local profile save failure does not invalidate a working capture.
      if (selectionId !== captureService.selectionId) return
      error.value = caught instanceof Error ? caught.message : String(caught)
    }
  }

  async function prepareOcrResources(): Promise<void> {
    if (disposed || !preset.value || !profile.value) return
    if (ocrResourcePreparation) return ocrResourcePreparation
    const targetPreset = preset.value
    const fingerprint = ocrFingerprint(targetPreset)
    if (readyOcrFingerprint === fingerprint) {
      ocrStatus.value = 'ready'
      ocrStage.value = 'ready'
      ocrProgress.value = 100
      return
    }
    if (preparedOcrFingerprint === fingerprint && retainedOcrPreparation) {
      if (ocrStatus.value !== 'initializing') {
        ocrStatus.value = 'prepared'
        ocrStage.value = 'prepared'
        ocrProgress.value = Math.max(80, ocrProgress.value)
      }
      return
    }

    const lifecycleRevision = ocrLifecycleRevision
    const runtimeBlock = ocrRuntimeBlock()
    if (runtimeBlock) {
      ocrStatus.value = 'error'
      ocrStage.value = 'checking'
      ocrProgress.value = 0
      ocrError.value = runtimeBlock
      ocrErrorType.value = runtimeBlock
      return
    }
    let preparationTask!: Promise<void>
    preparationTask = (async () => {
      releasePreparedOcrResources()
      ocrStatus.value = 'checking'
      ocrStartupDiagnostic.value = null
      ocrStage.value = 'checking'
      ocrProgress.value = 4
      ocrCachedModels.value = 0
      ocrError.value = null
      ocrErrorType.value = null
      ocrCacheDegraded.value = false
      const cached = new Set<string>()
      let pendingProgress: OcrCacheProgress | null = null
      let progressFrame = 0
      const cancelProgress = () => {
        if (progressFrame) cancelAnimationFrame(progressFrame)
        progressFrame = 0
        pendingProgress = null
      }
      cancelOcrProgress = cancelProgress
      const applyProgress = () => {
        progressFrame = 0
        const progress = pendingProgress
        pendingProgress = null
        if (!progress || lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        ocrStatus.value = 'downloading'
        ocrStage.value = progress.model
        ocrProgress.value = Math.max(5, Math.round(5 + progress.progress * 70))
        ocrCachedModels.value = cached.size
      }
      try {
        const preparation = await ensureOcrModelsCached(targetPreset.remoteResources.ocrModels, (progress) => {
          if (disposed || lifecycleRevision !== ocrLifecycleRevision) return
          if (progress.fromCache) cached.add(progress.model)
          pendingProgress = progress
          if (!progressFrame) progressFrame = requestAnimationFrame(applyProgress)
        })
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) {
          if (progressFrame) cancelAnimationFrame(progressFrame)
          pendingProgress = null
          preparation.releaseTemporaryResources()
          return
        }
        retainedOcrPreparation = preparation
        preparedOcrFingerprint = fingerprint
        ocrCacheDegraded.value = preparation.cacheDegraded
        if (progressFrame) cancelAnimationFrame(progressFrame)
        applyProgress()
        ocrStatus.value = 'prepared'
        ocrStage.value = 'prepared'
        ocrProgress.value = 80
      } catch (caught) {
        if (progressFrame) cancelAnimationFrame(progressFrame)
        pendingProgress = null
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        preparedOcrFingerprint = ''
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = isOcrResourceLoadError(caught) ? 'resource' : 'unknown'
      } finally {
        cancelProgress()
        if (cancelOcrProgress === cancelProgress) cancelOcrProgress = null
      }
    })().finally(() => {
      if (ocrResourcePreparation === preparationTask) ocrResourcePreparation = null
    })
    ocrResourcePreparation = preparationTask
    return preparationTask
  }

  async function initializeOcrEngine(): Promise<void> {
    if (disposed || !preset.value || !profile.value) return
    if (ocrEngineInitialization) return ocrEngineInitialization
    const targetPreset = preset.value
    const targetProfile = profile.value
    const fingerprint = ocrFingerprint(targetPreset)
    if (readyOcrFingerprint === fingerprint) {
      ocrStatus.value = 'ready'
      ocrStage.value = 'ready'
      ocrProgress.value = 100
      return
    }
    if (preparedOcrFingerprint !== fingerprint) return

    const lifecycleRevision = ocrLifecycleRevision
    let initializationTask!: Promise<void>
    initializationTask = (async () => {
      let recognizer: PresetRecognizer | null = null
      ocrStatus.value = 'initializing'
      ocrStage.value = 'initializing'
      ocrError.value = null
      ocrErrorType.value = null
      ocrStartupDiagnostic.value = null
      try {
        recognizer = targetPreset.createRecognizer(targetProfile)
        await recognizer.warmup()
        if (lifecycleRevision !== ocrLifecycleRevision
          || preset.value !== targetPreset
          || ocrFingerprint(targetPreset) !== fingerprint) return
        readyOcrFingerprint = fingerprint
        ocrStage.value = 'ready'
        ocrStatus.value = 'ready'
        ocrProgress.value = 100
        if (retainedOcrPreparation?.usedTemporaryResources && !cacheDegradedWarningShown) {
          cacheDegradedWarningShown = true
          ElMessage({
            type: 'warning',
            message: String(i18n.global.t('startup.cacheDegradedWarning')),
            duration: 5_000,
            showClose: true,
          })
        }
        releasePreparedOcrResources()
      } catch (caught) {
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = 'initialization'
        ocrStartupDiagnostic.value = caught instanceof OcrInitializationError ? caught.diagnostic : null
      } finally {
        recognizer?.dispose()
      }
    })().finally(() => {
      if (ocrEngineInitialization === initializationTask) ocrEngineInitialization = null
    })
    ocrEngineInitialization = initializationTask
    return initializationTask
  }

  async function activateCaptureRecognition(): Promise<void> {
    if (!isCapturing.value
      || ocrStatus.value !== 'ready'
      || !preset.value
      || !profile.value
      || activePipelineGeneration === captureGeneration) return

    const firstUse = firstUseHistory.state === 'pending' && Boolean(preset.value.locateLayout)
      && (!session.value || session.value.status === 'ended')
    startPipeline(session.value?.id ?? 'preview', null, captureGeneration, firstUse)
    if (firstUse) firstUseCalibrationPhase.value = 'waiting'
    if (session.value?.status !== 'interrupted' || !sessionEngine) return

    const resumedAt = Date.now()
    sessionEngine.resume(resumedAt, captureGeneration)
    journey.value = sessionEngine.journey()
    pipeline?.resetBaseline(undefined, sessionExpCheckpoint ?? undefined)
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    startLiveRateRefresh()
    await persistSession()
  }

  function releasePreparedOcrResources(): void {
    retainedOcrPreparation?.releaseTemporaryResources()
    retainedOcrPreparation = null
  }

  function releaseRecognitionForCaptureSelection(): Promise<void> {
    const currentPreset = preset.value
    const currentPipeline = pipeline
    ocrLifecycleRevision += 1
    ocrResourcePreparation = null
    ocrEngineInitialization = null
    currentPipeline?.stop()
    const engineRelease = currentPreset
      ? releaseSharedOcrEngine(currentPreset.remoteResources.ocrModels)
      : Promise.resolve()
    currentPipeline?.dispose()
    pipeline = null
    activePipelineGeneration = -1
    releasePreparedOcrResources()
    preparedOcrFingerprint = ''
    readyOcrFingerprint = ''
    setOcrIdle()
    return engineRelease
  }

  function setOcrIdle(): void {
    ocrStartupDiagnostic.value = null
    ocrStatus.value = 'idle'
    ocrStage.value = 'idle'
    ocrProgress.value = 0
    ocrCachedModels.value = 0
    ocrError.value = null
    ocrErrorType.value = null
    ocrCacheDegraded.value = false
  }

  async function interruptCapture(): Promise<void> {
    if (isEnding.value) {
      settlementController?.abort()
      return
    }
    startBaselineController?.abort()
    if (!sessionEngine || session.value?.status !== 'running') return
    pipeline?.stop()
    activePipelineGeneration = -1
    stopLiveRateRefresh()
    const interruptedAt = Date.now()
    session.value = sessionEngine.interrupt(interruptedAt)
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(interruptedAt)
    await persistSession()
  }

  async function persistSession(): Promise<void> {
    if (!session.value) return
    await saveSessionState(session.value, journey.value)
  }

  function startPipeline(sessionId: string, baseline: GameStateSnapshot | null = null, generation = captureGeneration, deferSampling = false): void {
    if (disposed || !preset.value || !profile.value) return
    pipeline?.dispose()
    let nextPipeline!: AnalysisPipeline
    nextPipeline = markRaw(new AnalysisPipeline(
      preset.value,
      profile.value,
      onPipelineResult,
      () => recoverPipeline(nextPipeline),
      performanceConfigs[appliedPerformanceTier.value].processingFpsLimit,
      captureIntervalMs,
      { save: saveCalibrationProfile, loadSize: loadCalibrationSize },
      (frame, engine) => {
        if (pipeline !== nextPipeline || isStarting.value || isEnding.value || isInitializingCalibration.value
          || firstUseCalibrationBlocking.value || !isCapturing.value) return
        captureService.rankCheck.inspect(frame, frame.videoWidth, frame.videoHeight, engine)
      },
    ))
    pipeline = nextPipeline
    activePipelineGeneration = generation
    nextPipeline.setDiagnosticMetric(calibrationMetric)
    nextPipeline.start(captureService.video, sessionId, baseline, generation,
      baseline === null && session.value && session.value.status !== 'ended' ? sessionExpCheckpoint ?? undefined : undefined, { deferSampling })
  }

  function startLiveRateRefresh(): void {
    if (disposed) return
    stopLiveRateRefresh()
    lastLiveRateRefreshAt = 0
    liveRateRefreshTimer = window.setInterval(() => {
      refreshLiveRates()
    }, 1_000)
  }

  function stopLiveRateRefresh(): void {
    if (!liveRateRefreshTimer) return
    window.clearInterval(liveRateRefreshTimer)
    liveRateRefreshTimer = 0
  }

  function refreshLiveRates(now = Date.now()): void {
    if (session.value?.status !== 'running' || !sessionEngine) return
    if (now - lastLiveRateRefreshAt < 1_000) return
    lastLiveRateRefreshAt = now
    session.value = sessionEngine.current(now)
    liveRates.value = sessionEngine.liveRates(now)
  }

  function handlePipHeartbeat(): void {
    if (isSelectingWindow.value || ocrStatus.value !== 'ready') return
    pipeline?.pulse()
    refreshLiveRates()
  }

  function recoverPipeline(expectedPipeline: AnalysisPipeline | null = pipeline): void {
    if (firstUseCalibrationBusy.value) return
    if (recoveringPipeline
      || !expectedPipeline
      || pipeline !== expectedPipeline
      || !isCapturing.value
      || !preset.value
      || !profile.value
      || isStarting.value
      || isEnding.value) return

    recoveringPipeline = true
    try {
      captureGeneration += 1
      if (session.value?.status === 'running' && sessionEngine) {
        const now = Date.now()
        session.value = sessionEngine.reconnectCapture(now, captureGeneration)
        liveRates.value = sessionEngine.liveRates(now)
      }
      mediaContinuityLost = false
      pageHiddenAt = null
      const sessionId = session.value?.id ?? 'preview'
      startPipeline(sessionId, gameState.value, captureGeneration)
      pipeline?.pulse(true)
    } finally {
      recoveringPipeline = false
    }
  }

  function handleCaptureMediaState(state: CaptureMediaState): void {
    if (mediaWaitingTimer !== null) { clearTimeout(mediaWaitingTimer); mediaWaitingTimer = null }
    if (state === 'waiting' || state === 'stalled') {
      mediaWaitingTimer = setTimeout(() => {
        mediaWaitingTimer = null
        if (!isCapturing.value) return
        const health = pipeline?.health()
        if (health?.lastSuccessfulAt != null && Date.now() - health.lastSuccessfulAt < continuityGapMs()) return
        mediaContinuityLost = true
        startBaselineController?.abort()
        invalidateFirstUseCalibration()
        recoverPipeline()
      }, continuityGapMs())
      return
    }
    if (state === 'paused' || state === 'muted' || state === 'idle') {
      mediaContinuityLost = true
      startBaselineController?.abort()
      invalidateFirstUseCalibration()
      return
    }
    if (state !== 'playing' && state !== 'unmuted') return

    const shouldRebaseline = mediaContinuityLost
    mediaContinuityLost = false
    if (shouldRebaseline) recoverPipeline()
    else pipeline?.pulse(true)
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      pageHiddenAt ??= Date.now()
      return
    }
    handlePageResume()
  }

  function handlePageFreeze(): void {
    pageWasFrozen = true
    startBaselineController?.abort()
    if (session.value && session.value.status !== 'ended') {
      void persistSession().catch(() => undefined)
    }
  }

  function captureIntervalMs(): number { return captureService.frameIntervalMs ?? 1_000 }
  function continuityGapMs(): number {
    return Math.max(BACKGROUND_CONTINUITY_GAP_MS, 3 * Math.max(captureIntervalMs(), 1_000 / (performanceConfigs[appliedPerformanceTier.value].processingFpsLimit || 8)) + 2_000)
  }
  function recognitionPerformanceStatus() { return pipeline?.performanceState?.() ?? null }

  function handlePageResume(): void {
    if (firstUseCalibrationBusy.value) return
    if (!isCapturing.value || !pipeline) {
      pageWasFrozen = false
      if (document.visibilityState === 'visible') pageHiddenAt = null
      return
    }
    if (pageWasFrozen) {
      pageWasFrozen = false
      pageHiddenAt = null
      recoverPipeline()
      return
    }

    const now = Date.now()
    const health = pipeline.health()
    const continuityGap = continuityGapMs()
    const missedBackgroundFrames = pageHiddenAt !== null
      && now - pageHiddenAt >= continuityGap
      && (health?.lastSuccessfulAt === null
        || health?.lastSuccessfulAt === undefined
        || now - health.lastSuccessfulAt >= continuityGap)
    pageHiddenAt = null
    if (missedBackgroundFrames) recoverPipeline()
    else pipeline.pulse(true)
  }

  function restrictSessionRankForPerformance(): void {
    if (!sessionEngine || !session.value || session.value.status === 'ended') return
    session.value = sessionEngine.restrictRankForPerformance(appliedPerformanceTier.value, captureFpsApplied.value) ?? session.value
  }

  function restrictSessionRankForSource(): void {
    const assessment = captureService.rankCheck.assessment
    if (!assessment || assessment.selectionId !== captureService.selectionId || captureService.status !== 'active'
      || !sessionEngine || !session.value || session.value.status === 'ended' || pendingFinalization.value) return
    session.value = sessionEngine.restrictRankForSource(rankSourceRestriction(assessment)) ?? session.value
  }

  function onPipelineResult(result: PipelineResult): void {
    if (disposed) return
    if (result.captureGeneration !== captureGeneration) return
    latestObservation = result.observation
    latestObservationGeneration = result.captureGeneration
    gameState.value = result.state
    hudLocation.value = result.hud
    if (session.value?.status !== 'running' || !sessionEngine) {
      rawReading.value = result.raw
      return
    }
    const previousTrustedGain = session.value.aggregate.expRateGained ?? 0
    sessionEngine.setExpSamplingInterval(Math.max(
      profile.value?.recognition.metricRecognitionIntervalsMs?.exp ?? 1_000,
      captureIntervalMs(),
      1_000 / (performanceConfigs[appliedPerformanceTier.value].processingFpsLimit || 8),
    ))
    sessionEngine.setExpReadoutInterval(Math.max(
      performanceConfigs[appliedPerformanceTier.value].metricRecognitionIntervalsMs.exp,
      performanceConfigs[appliedPerformanceTier.value].metricRecognitionIntervalsMs.level,
      captureIntervalMs(), pipeline?.samplingIntervalMs?.() ?? 0,
    ))
    restrictSessionRankForPerformance()
    const ingested = sessionEngine.ingestWithAccounting(result.state, result.events, result.observation, result.captureGeneration,
      profile.value?.recognition.minConfidence ?? .62)
    sessionExpCheckpoint = pipeline?.expCheckpoint() ?? sessionExpCheckpoint
    const displayExp = sessionEngine.expDisplayStatus()
    const expDiagnostic = result.raw.diagnostics.metrics?.exp
    const expAdmission = result.observation.expCandidateAdmission ?? result.observation.expEvidenceAdmission
    rawReading.value = expDiagnostic
      ? {
          ...result.raw,
          diagnostics: {
            ...result.raw.diagnostics,
            metrics: {
              ...result.raw.diagnostics.metrics,
              exp: {
                ...expDiagnostic,
                expAdmission: expAdmission ? { ...expAdmission,
                  creditedDelta: Math.max(0, (ingested.record.aggregate.expRateGained ?? 0) - previousTrustedGain) } : undefined,
                expDisplay: displayExp,
              },
            },
          },
        }
      : result.raw
    session.value = ingested.record
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(result.state.capturedAt)
    void recovery.flush(session.value, journey.value).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }

  async function acquireSessionStartBaseline(): Promise<SessionStartBaselineResult> {
    const requestedAt = Date.now()
    const existing = currentSessionStartBaseline(requestedAt, SESSION_START_BASELINE_MAX_AGE_MS)
    if (existing.status === 'ready') {
      return { status: 'ready', baseline: existing.baseline, startedAt: requestedAt, requestedAt }
    }
    const targetPipeline = pipeline
    const targetGeneration = captureGeneration
    if (!targetPipeline || !isCapturing.value) return { status: 'capture-unavailable', baseline: null, requestedAt }

    const reusableCandidate = latestObservationGeneration === targetGeneration
      && latestObservation?.expExactCandidate
      && requestedAt >= latestObservation.expExactCandidate.sourceAt
      && requestedAt - latestObservation.expExactCandidate.sourceAt <= SESSION_START_BASELINE_MAX_AGE_MS
      && latestObservation.expExactCandidate.levelSourceAt !== null
      && requestedAt >= latestObservation.expExactCandidate.levelSourceAt
      && requestedAt - latestObservation.expExactCandidate.levelSourceAt <= SESSION_START_BASELINE_MAX_AGE_MS
      ? latestObservation.expExactCandidate
      : undefined

    const controller = new AbortController()
    startBaselineController = controller
    const consensus = new SessionStartExpConsensus(
      requestedAt,
      profile.value?.recognition.minConfidence ?? 0.62,
      reusableCandidate,
    )
    const initialLevelDiagnostic = latestObservationGeneration === targetGeneration
      ? latestObservation?.diagnostics.metrics?.level
      : undefined
    let levelCandidate = typeof initialLevelDiagnostic?.candidateValue === 'number'
      ? initialLevelDiagnostic.candidateValue
      : null
    let levelCandidateAt = levelCandidate === null
      ? null
      : initialLevelDiagnostic?.pendingSince ?? latestObservation?.level.capturedAt ?? null
    let levelConfirmedSamples: 0 | 1 | 2 = levelCandidate === null ? 0 : 1
    let levelReviewFailureReason = initialLevelDiagnostic?.reviewFailureReason
    sessionStartProgress.value = {
      phase: 'confirming',
      requestedAt,
      elapsedMs: 0,
      confirmedSamples: existing.status === 'level-unconfirmed' ? levelConfirmedSamples : consensus.sampleCount,
      targetSamples: 2,
      currentMetric: existing.status === 'level-unconfirmed' ? 'level' : 'exp',
      ...(existing.status === 'level-unconfirmed' && levelCandidate !== null ? { candidate: levelCandidate } : {}),
      ...(existing.status === 'level-unconfirmed' && levelReviewFailureReason ? { reviewFailureReason: levelReviewFailureReason } : {}),
      ...(existing.status !== 'level-unconfirmed' && consensus.confirmationFailureReason
        ? { confirmationFailureReason: consensus.confirmationFailureReason } : {}),
    }
    let latest: SessionStartBaselineEvaluation = existing
    let completedFreshSample = false
    let latestCandidateAt: number | null = reusableCandidate?.sourceAt ?? null
    let latestCandidateAbsolute: number | null = reusableCandidate?.value.absolute ?? null
    let forceLevelSample = reusableCandidate === undefined
    let softWaitId = 0
    let noProgressTimeoutId = 0
    const absoluteTimeoutId = window.setTimeout(() => controller.abort('absolute-timeout'), SESSION_START_BASELINE_ABSOLUTE_TIMEOUT_MS)
    const cancelled = new Promise<null>((resolve) => {
      controller.signal.addEventListener('abort', () => resolve(null), { once: true })
    })

    const updateProgress = (currentMetric: 'level' | 'exp', lastInferenceDurationMs?: number) => {
      const previous = sessionStartProgress.value
      if (!previous || previous.requestedAt !== requestedAt) return
      const {
        candidate: _candidate,
        reviewFailureReason: _reviewFailureReason,
        confirmationFailureReason: _confirmationFailureReason,
        ...base
      } = previous
      sessionStartProgress.value = {
        ...base,
        elapsedMs: Date.now() - requestedAt,
        confirmedSamples: currentMetric === 'level' ? levelConfirmedSamples : consensus.sampleCount,
        currentMetric,
        ...(currentMetric === 'level' && levelCandidate !== null ? { candidate: levelCandidate } : {}),
        ...(currentMetric === 'level' && levelReviewFailureReason ? { reviewFailureReason: levelReviewFailureReason } : {}),
        ...(currentMetric === 'exp' && consensus.confirmationFailureReason
          ? { confirmationFailureReason: consensus.confirmationFailureReason } : {}),
        ...(lastInferenceDurationMs === undefined ? {} : { lastInferenceDurationMs }),
      }
    }

    const armNoProgressTimeout = () => {
      window.clearTimeout(noProgressTimeoutId)
      noProgressTimeoutId = window.setTimeout(() => controller.abort('no-progress-timeout'), SESSION_START_BASELINE_HARD_TIMEOUT_MS)
    }

    let currentStage: SessionSamplingStage | null = null
    const updateStage = (stage: SessionSamplingStage) => {
      if (controller.signal.aborted || startBaselineController !== controller || currentStage === stage) return
      currentStage = stage
      window.clearTimeout(softWaitId)
      const budgetMs = stage === 'waiting-frame'
        ? samplingGapBudgetMs(targetPipeline.samplingIntervalMs?.() ?? Math.max(captureIntervalMs(), 1_000 / (performanceConfigs[appliedPerformanceTier.value].processingFpsLimit || 8)))
        : SESSION_START_BASELINE_SOFT_WAIT_MS
      performanceDiagnostics.setStartWait({ stage, since: Date.now(), ...(stage === 'confirming' ? {} : { budgetMs }) })
      const previous = sessionStartProgress.value
      if (previous) sessionStartProgress.value = { ...previous, phase: 'confirming', stage, slowReason: undefined, elapsedMs: Date.now() - requestedAt }
      if (stage === 'confirming') return
      softWaitId = window.setTimeout(() => {
        const progress = sessionStartProgress.value
        if (!progress || controller.signal.aborted || startBaselineController !== controller) return
        sessionStartProgress.value = { ...progress, phase: 'slow', stage,
          slowReason: stage === 'waiting-frame' ? 'frame' : 'ocr', elapsedMs: Date.now() - requestedAt }
      }, budgetMs)
    }

    const collect = async (): Promise<SessionStartBaselineResult | null> => {
      armNoProgressTimeout()
      updateStage('recognizing')
      await targetPipeline.prepareForSessionStart(controller.signal, updateStage)
      if (controller.signal.aborted) return null
      updateStage('confirming')

      while (!controller.signal.aborted) {
        if (pipeline !== targetPipeline || captureGeneration !== targetGeneration || !isCapturing.value) {
          return { status: 'capture-unavailable', baseline: null, requestedAt }
        }

        const needsLevel = forceLevelSample || latest.status === 'level-unconfirmed'
        const requestedMetrics: readonly MetricKey[] = needsLevel ? ['level'] : ['exp']
        updateProgress(needsLevel ? 'level' : 'exp')
        updateStage('recognizing')
        try {
          const sampled = await targetPipeline.sampleForSessionStart(captureService.video, requestedMetrics, controller.signal, updateStage)
          completedFreshSample ||= sampled
          updateStage('confirming')
        } catch {
          updateStage('confirming')
          if (controller.signal.aborted) break
        }
        if (pipeline !== targetPipeline || captureGeneration !== targetGeneration || !isCapturing.value) {
          return { status: 'capture-unavailable', baseline: null, requestedAt }
        }

        latest = currentSessionStartBaseline(Date.now(), null, requestedAt)
        const levelDiagnostic = latestObservationGeneration === targetGeneration
          ? latestObservation?.diagnostics.metrics?.level
          : undefined
        const observedLevelCandidate = latestObservationGeneration === targetGeneration
          && typeof latestObservation?.level.ocrValue === 'number'
          && latestObservation.level.capturedAt >= requestedAt
          ? latestObservation.level.ocrValue
          : null
        const observedLevelAt = observedLevelCandidate === null ? null : latestObservation!.level.capturedAt
        if (observedLevelCandidate !== null && observedLevelAt !== null && observedLevelAt !== levelCandidateAt) {
          const previousLevelSamples = levelConfirmedSamples
          levelConfirmedSamples = levelCandidate === observedLevelCandidate
            ? Math.min(2, levelConfirmedSamples + 1) as 0 | 1 | 2
            : 1
          levelCandidate = observedLevelCandidate
          levelCandidateAt = observedLevelAt
          levelReviewFailureReason = undefined
          if (latestObservation?.level.confirmation === 'consensus'
            && latestObservation.level.value === observedLevelCandidate) {
            levelConfirmedSamples = 2
          }
          if (levelConfirmedSamples > previousLevelSamples) armNoProgressTimeout()
        } else if (needsLevel && levelDiagnostic?.lastAttemptAt !== undefined
          && levelDiagnostic.lastAttemptAt >= requestedAt) {
          levelReviewFailureReason = levelDiagnostic.reviewFailureReason
        }
        const candidate = latestObservationGeneration === targetGeneration
          ? latestObservation?.expExactCandidate
          : undefined
        const previousCandidateAt = latestCandidateAt
        const previousCandidateAbsolute = latestCandidateAbsolute
        const previousSampleCount = consensus.sampleCount
        const confirmation = consensus.observe(candidate)
        if (candidate && candidate.sourceAt >= requestedAt && candidate.sourceAt !== previousCandidateAt) {
          latestCandidateAt = candidate.sourceAt
          latestCandidateAbsolute = candidate.value.absolute
          if (confirmation.sampleCount > previousSampleCount) armNoProgressTimeout()
        }
        const duration = latestObservation?.diagnostics.metrics?.[needsLevel ? 'level' : 'exp']?.durationMs
        updateProgress(confirmation.sampleCount > 0 ? 'exp' : needsLevel ? 'level' : 'exp', duration)
        if (confirmation.status === 'ready' && confirmation.confirmed) {
          const baseline = confirmedSessionStartBaseline(confirmation.confirmed, requestedAt)
          if (baseline) {
            return { status: 'ready', baseline, startedAt: confirmation.confirmed.sourceAt, requestedAt }
          }
        }

        forceLevelSample = candidate?.level === null
          || (previousCandidateAt !== null
            && candidate !== undefined
            && candidate.sourceAt !== previousCandidateAt
            && previousCandidateAbsolute !== null
            && candidate.value.absolute < previousCandidateAbsolute)

        await waitForNextSessionStartFrame(controller.signal)
      }
      return null
    }

    let result: SessionStartBaselineResult | null = null
    try {
      result = await Promise.race([collect(), cancelled])
    } finally {
      performanceDiagnostics.setStartWait(null)
      window.clearTimeout(softWaitId)
      window.clearTimeout(noProgressTimeoutId)
      window.clearTimeout(absoluteTimeoutId)
      const abortReason = controller.signal.reason
      if (!controller.signal.aborted) controller.abort('complete')
      if (startBaselineController === controller) startBaselineController = null
      if (sessionStartProgress.value?.requestedAt === requestedAt) sessionStartProgress.value = null

      if (!result && abortReason === 'user') {
        result = { status: 'cancelled', baseline: null, requestedAt }
      }
    }

    if (!result) {
      const hardTimedOut = controller.signal.reason === 'no-progress-timeout'
        || controller.signal.reason === 'absolute-timeout'
      const captureUnavailable = pipeline !== targetPipeline
        || captureGeneration !== targetGeneration
        || !isCapturing.value
        || mediaContinuityLost
        || pageWasFrozen
      const status = captureUnavailable
        ? 'capture-unavailable'
        : consensus.confirmationFailureReason
          ? 'exp-conflict'
        : consensus.sampleCount === 1
          ? 'exp-confirming'
        : !completedFreshSample || latest.status === 'capture-unavailable'
          ? 'stale'
          : latest.status
      result = {
        status,
        baseline: null,
        requestedAt,
        ...(hardTimedOut ? { hardTimedOut: true } : {}),
        confirmedSamples: status === 'level-unconfirmed'
          ? levelConfirmedSamples === 1 ? 1 : 0
          : consensus.sampleCount === 1 ? 1 : 0,
        ...(status !== 'level-unconfirmed' || levelCandidate === null ? {} : { levelCandidate }),
        ...(status === 'level-unconfirmed' && levelReviewFailureReason
          ? { reviewFailureReason: levelReviewFailureReason }
          : {}),
        ...(status === 'exp-conflict' && consensus.confirmationFailureReason
          ? { confirmationFailureReason: consensus.confirmationFailureReason } : {}),
      }
    }
    if (result.status !== 'ready'
      && pipeline === targetPipeline
      && isCapturing.value
      && !mediaContinuityLost
      && !pageWasFrozen) {
      targetPipeline.resume(captureService.video)
      targetPipeline.pulse(true)
    }
    return result
  }

  function confirmedSessionStartBaseline(
    confirmed: ConfirmedSessionStartExp,
    requestedAt: number,
  ): GameStateSnapshot | null {
    const state = gameState.value
    if (!state
      || latestObservationGeneration !== captureGeneration
      || confirmed.sourceAt < requestedAt) return null
    const observation = latestObservation
    const mesosFresh = Boolean(
      observation?.accepted.mesos
      && observation.mesos.value !== null
      && state.mesos === observation.mesos.value
      && state.observedAt?.mesos === observation.mesos.capturedAt
      && confirmed.sourceAt >= observation.mesos.capturedAt
      && confirmed.sourceAt - observation.mesos.capturedAt <= SESSION_START_BASELINE_MAX_AGE_MS,
    )
    return {
      ...state,
      capturedAt: confirmed.sourceAt,
      level: confirmed.level,
      exp: confirmed.value,
      mesos: mesosFresh ? state.mesos : null,
      observedAt: {
        hp: state.observedAt?.hp ?? null,
        mp: state.observedAt?.mp ?? null,
        mapName: state.observedAt?.mapName ?? null,
        level: confirmed.levelSourceAt,
        exp: confirmed.sourceAt,
        mesos: mesosFresh ? observation!.mesos.capturedAt : null,
      },
      confidence: {
        ...state.confidence,
        exp: confirmed.confidence,
      },
    }
  }

  function currentSessionStartBaseline(
    now: number,
    maximumAgeMs: number | null,
    minimumCapturedAt?: number,
  ): SessionStartBaselineEvaluation {
    if (!preset.value) {
      return { status: 'capture-unavailable' as const, baseline: null, sourceAt: null }
    }
    return evaluateSessionStartBaseline(gameState.value, latestObservation, {
      now,
      captureGeneration,
      observationGeneration: latestObservationGeneration,
      maximumAgeMs,
      ...(minimumCapturedAt === undefined ? {} : { minimumCapturedAt }),
      mesosMaximumAgeMs: SESSION_START_BASELINE_MAX_AGE_MS,
    })
  }

  function waitForNextSessionStartFrame(signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
      let settled = false
      let callbackId: number | null = null
      const timeoutId = window.setTimeout(finish, SESSION_START_RETRY_DELAY_MS)
      const onAbort = () => finish()

      function finish(): void {
        if (settled) return
        settled = true
        window.clearTimeout(timeoutId)
        if (callbackId !== null) captureService.video.cancelVideoFrameCallback?.(callbackId)
        signal.removeEventListener('abort', onAbort)
        resolve()
      }

      if (signal.aborted) {
        finish()
        return
      }
      signal.addEventListener('abort', onAbort, { once: true })
      if (captureService.video.requestVideoFrameCallback) {
        callbackId = captureService.video.requestVideoFrameCallback(() => finish())
      }
    })
  }

  function sessionStartIssue(
    status: Exclude<SessionStartBaselineStatus, 'ready'>,
    requestedAt: number,
    hardTimedOut = false,
    confirmedSamples: 0 | 1 = 0,
    levelCandidate?: number,
    reviewFailureReason?: MetricRecognitionFailureReason,
    confirmationFailureReason?: ExpConfirmationFailureReason,
  ): SessionStartIssue {
    const metric = status === 'level-unconfirmed'
      ? 'level'
      : status === 'exp-incomplete' || status === 'exp-confirming' || status === 'exp-conflict'
        ? 'exp'
        : null
    const diagnostic = metric ? latestObservation?.diagnostics.metrics?.[metric] : undefined
    const freshDiagnostic = diagnostic?.lastAttemptAt !== undefined && diagnostic.lastAttemptAt >= requestedAt
      ? diagnostic
      : undefined
    const completion = metric === 'level' && diagnostic?.completion && diagnostic.completion.capturedAt >= requestedAt
      ? diagnostic.completion : undefined
    const reason: SessionStartIssue['reason'] = status === 'capture-unavailable'
      ? 'capture-unavailable'
      : status === 'stale'
        ? hardTimedOut ? 'unresponsive' : 'slow'
        : status === 'exp-incomplete'
          ? 'incomplete'
          : status === 'exp-conflict'
            ? 'conflict'
            : freshDiagnostic?.failureReason === 'parse-failed'
              && metric === 'level'
              && /LV/i.test(freshDiagnostic.rawText ?? '')
              ? 'mixed-content'
              : freshDiagnostic?.failureReason ?? 'confirming'
    const diagnosticCandidate = freshDiagnostic?.candidateValue ?? freshDiagnostic?.value
    const candidateValue = levelCandidate ?? diagnosticCandidate
    const candidate = metric === 'level' && typeof candidateValue === 'number'
      ? candidateValue
      : undefined
    const effectiveReviewFailure = metric === 'level'
      ? reviewFailureReason ?? freshDiagnostic?.reviewFailureReason
      : undefined
    const effectiveConfirmationFailure = metric === 'exp'
      ? confirmationFailureReason ?? freshDiagnostic?.confirmationFailureReason
      : undefined
    return {
      metric,
      reason,
      requestedAt,
      ...(typeof candidate === 'number' ? { candidate } : {}),
      ...(completion ? { completion } : {}),
      ...(freshDiagnostic?.rawText ? { rawText: freshDiagnostic.rawText } : {}),
      ...(freshDiagnostic?.selectedText ? { selectedText: freshDiagnostic.selectedText } : {}),
      ...(freshDiagnostic?.confidence !== undefined ? { confidence: freshDiagnostic.confidence } : {}),
      ...(diagnostic?.roi ? { roi: diagnostic.roi } : {}),
      ...(freshDiagnostic?.recognitionPath ? { recognitionPath: freshDiagnostic.recognitionPath } : {}),
      confirmedSamples,
      ...(effectiveReviewFailure ? { reviewFailureReason: effectiveReviewFailure } : {}),
      ...(effectiveConfirmationFailure ? { confirmationFailureReason: effectiveConfirmationFailure } : {}),
    }
  }

  function dismissStartIssue(): void {
    startIssue.value = null
  }

  function cancelSessionStart(): void {
    if (!startBaselineController || startBaselineController.signal.aborted) return
    startBaselineController.abort('user')
    sessionStartProgress.value = null
  }

  function setCaptureStartupPhase(phase: CaptureStartupPhase): void {
    captureStartupPhase.value = phase
    try {
      localStorage.setItem(CAPTURE_STARTUP_PHASE_KEY, JSON.stringify({ phase, recordedAt: Date.now() }))
    } catch {
      // Startup diagnostics are optional and never block capture.
    }
  }

  async function waitForNativePickerExit(): Promise<void> {
    await nextAnimationFrame()
    await nextAnimationFrame()
    await new Promise<void>((resolve) => window.setTimeout(resolve, 100))
  }

  function nextAnimationFrame(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve())
      else window.setTimeout(resolve, 0)
    })
  }

  function yieldToMainThread(): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, 0))
  }

  function createProfile(gamePreset: GamePreset): CaptureProfile {
    const now = Date.now()
    return {
      id: crypto.randomUUID(),
      name: 'Default',
      presetId: gamePreset.id,
      presetVersion: gamePreset.version,
      ...(gamePreset.layoutVersion ? { layoutVersion: gamePreset.layoutVersion, autoRois: {} } : {}),
      sourceWidth: 1368,
      sourceHeight: 800,
      hudRect: gamePreset.hud.fallback,
      roiOverrides: {},
      colorCalibration: {},
      recognition: migrateRecognitionFrequencies({
        captureFps: DEFAULT_CAPTURE_FPS,
        metricRecognitionIntervalsMs: recommendedMetricRecognitionIntervalsMs(),
        metricRecognitionIntervalsVersion: METRIC_RECOGNITION_INTERVAL_VERSION,
        minConfidence: 0.62,
        ocrChangeThreshold: 0.025,
      }),
      createdAt: now,
      updatedAt: now,
    }
  }

  function ocrFingerprint(gamePreset: GamePreset): string {
    const models = gamePreset.remoteResources.ocrModels
    return `${models.detection.sha256}|${models.recognition.sha256}`
  }

  function captureSourceChanged(previous: CaptureProfile, next: CaptureProfile): boolean {
    return previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.roiOverrides) !== JSON.stringify(next.roiOverrides)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
  }

  function changedAdaptiveMetric(previous: CaptureProfile, next: CaptureProfile): RecognitionMetricKey | null {
    const hasNonRoiChange = previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
    if (hasNonRoiChange) return null
    const changed = recognitionMetricKeys.filter((metric) => JSON.stringify(previous.roiOverrides[metric]) !== JSON.stringify(next.roiOverrides[metric]))
    return changed.length === 1 ? changed[0] ?? null : null
  }

  return {
    isInitializingCalibration, initializeCalibration,
    firstUseCalibrationPhase, firstUseCalibrationOpen, firstUseCalibrationProgress, firstUseCalibrationOutcome,
    firstUseCalibrationFrame, firstUseCalibrationRetained, firstUseCalibrationBlocking, firstUseCalibrationBusy,
    runFirstUseCalibration, skipFirstUseCalibration, closeFirstUseCalibration,
    recognitionPerformanceStatus, appliedPerformanceTier, captureFpsApplied, performanceChangeDeferred, captureFrequencyPending,
    preset, profile, session, liveRates, gameState, rawReading, hudLocation, captureStatus, captureLabel, captureFailure, captureDiagnostic, capturePreference, captureStartupPhase, error, needsCalibration,
    ocrStatus, ocrStage, ocrProgress, ocrCachedModels, ocrError, ocrErrorType, ocrStartupDiagnostic, ocrCacheDegraded, journey, video, startIssue, sessionStartProgress,
    isCapturing, isSelectingWindow, canSelectWindow, captureSupported, captureCompatibilityIssue, isRunning, isPaused, isStarting, isEnding, isPreparingForReload, hasPendingFinalization,
    pipSupported, pipAvailability, pipIssue, isOpeningPip, pipDisabledReason, dismissPipIssue, refreshPipAvailability,
    initialize, loadPreset, selectWindow, startSession, pauseSession, resumeSession, endSession, updateProfile, setCalibrationMetric, openPip, initializeOcr, dismissStartIssue, cancelSessionStart,
  }
})
